/**
 * Where the first-visit splash floor is counted from (job #773, speed red
 * team #766 §3; Watson's recommendation, adopted in the commission).
 *
 * The first-visit cinematic holds "ready" for a floor of 2800 ms. It used to
 * count that floor from the player's MOUNT. On a slow phone the stranger had
 * already waited about 4.7 s for the player to appear, and then waited out the
 * whole floor again. Counted from the TAP instead, a fast phone still sees the
 * animation, and a slow one has already waited out the floor by the time the
 * player appears. The floor value and the animation stages are unchanged.
 *
 * "The tap" is the navigation start. A first visit is always a fresh document
 * load (a shared link, or opening the app), and `performance.now()` counts
 * from that navigation. There is no earlier anchor on any entry. A return
 * user or an in-app course switch keeps the mount anchor (its floor is 0 since job #298).
 */
export function splashElapsedMs(opts: {
  /** Return user or in-app remount: counted from mount (the floor is 0 since job #298). */
  skipCinematic: boolean
  /** ms since the player's onMounted started. */
  sinceMountMs: number
  /** ms since navigation start, i.e. performance.now(). */
  sinceTapMs: number
}): number {
  if (opts.skipCinematic) return opts.sinceMountMs
  return Number.isFinite(opts.sinceTapMs) && opts.sinceTapMs >= opts.sinceMountMs
    ? opts.sinceTapMs
    : opts.sinceMountMs
}

/**
 * How long each first-visit stage holds (job #983). The two stages, awakening
 * 800 ms and finding 900 ms, used to run from the player's mount whatever the
 * time already spent, so a slow phone that reached the player 4.5 s after the
 * tap waited 1.7 s more after #773 had moved the floor to the tap. They now
 * fit inside what is left of the floor, counted from the same tap: a fast
 * phone sees them at full length, a slow one that has already waited out the
 * floor goes straight on.
 */
export const CINEMATIC_STAGES_MS = [800, 900] as const

export function cinematicStageMs(opts: {
  /** ms since navigation start, i.e. performance.now(), when the stages begin. */
  sinceTapMs: number
  /** The first-visit floor. */
  floorMs: number
}): number[] {
  const full = CINEMATIC_STAGES_MS.reduce((a, b) => a + b, 0)
  if (!Number.isFinite(opts.sinceTapMs)) return [...CINEMATIC_STAGES_MS]
  const budget = Math.max(0, opts.floorMs - opts.sinceTapMs)
  if (budget >= full) return [...CINEMATIC_STAGES_MS]
  return CINEMATIC_STAGES_MS.map((s) => Math.round((s * budget) / full))
}

/**
 * The minimum time the splash holds the play button back (job #298).
 *
 * Tom, 2026-10-16 (r-2026-10-16-ssi-welcome-the-typed-welcome-welcome): a
 * brand-new learner gets an actual welcome, typed — "Welcome to SaySomethingin
 * <Language>" — and keeps its typing time: 2800 ms, counted from the tap. Anyone
 * who has been here before, or an in-app remount, gets a quick non-blocking
 * "Welcome back…" or "Getting your <Language> course details" and NO floor: the
 * first intro plays the moment it is ready ("no boot hold-back at all").
 * The load monitor's `fresh` bar allows the welcome (tools/load-speed/logic.mjs
 * BRAND_WELCOME_MS); every other state is held to 3 s.
 */
export const BRAND_WELCOME_FLOOR_MS = 2800

export function splashFloorMs(opts: { skipCinematic: boolean }): number {
  return opts.skipCinematic ? 0 : BRAND_WELCOME_FLOOR_MS
}
